import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import AgilisServices, { GenerateBilletsResponse, GenerateInstallmentsBilletsPayload } from "../../../services/AgilisServices";
import ConsultarService, { Inscricao, Parcela } from "../../../services/ConsultarService";
import { isServerError } from "../../../config/api";
import { useQuery } from "../../../helpers/Utils";
import Format from "../../../helpers/Format";
import Alert from "../../Alert";
import Button from "../../Button";
import Loader from "../../Loader";
import Modal from "../../Modal";
import Tooltip from "../../Tooltip";
import ItemParcela from "../ItemParcela";
import PaymentModal from "../PaymentModal";
import BilletConfirmationModal from "../BilletConfirmationModal";
import style from "./styles.module.scss";

interface Props {
  id: number;
  numero: string;
  data: string;
  valor: number;
  valorPago: number;
  totalParcelas: number;
  status: string;
  status_i18n: string;
  opened?: boolean;
}

function normalizeStatus(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function sortByDueDate(installments: Parcela[]): Parcela[] {
  return [...installments].sort((first, second) => {
    return moment(first.due_date).valueOf() - moment(second.due_date).valueOf();
  });
}

const keyI18n = "translations.consultInstallments.paymentRequestItem";

export default function ItemParcelamento({
  id,
  numero,
  data,
  valor,
  valorPago,
  totalParcelas,
  status,
  status_i18n,
  opened,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingInscricoes, setLoadingInscricoes] = useState(false);
  const [loadingBoleto, setLoadingBoleto] = useState(false);
  const [hasLoadedInstallments, setHasLoadedInstallments] = useState(false);
  const [show, setShow] = useState(false);
  const [showModalBoleto, setShowModalBoleto] = useState(false);
  const [showGenerateBilletDueTodayAlert, setShowGenerateBilletDueTodayAlert] = useState(false);
  const [alertOverdueInstallments, setAlertOverdueInstallments] = useState(false);
  const [hasPreviousBillet, setHasPreviousBillet] = useState(false);
  const [showOverdueWarning, setShowOverdueWarning] = useState(false);
  const [parcelasAtrasadas, setParcelasAtrasadas] = useState<Parcela[]>([]);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<Parcela[]>([]);
  const [overdueInstallmentBilletDueDate, setOverdueInstallmentBilletDueDate] = useState<string>();
  const [showModalInscricoes, setShowModalInscricoes] = useState(false);
  const [error, setError] = useState("");
  const [errorBoleto, setErrorBoleto] = useState("");
  const [errorInscricoes, setErrorInscricoes] = useState("");
  const [parcelamentoId, setParcelamentoId] = useState<number>(id);
  const [parcelasPagas, setParcelasPagas] = useState<Parcela[]>([]);
  const [parcelasNaoPagas, setParcelasNaoPagas] = useState<Parcela[]>([]);
  const [inscricoes, setInscricoes] = useState<Inscricao[]>([]);
  const [payment, setPayment] = useState<GenerateBilletsResponse>();
  const [pixPayment, setPixPayment] = useState(false);
  const autoLoadAttempted = useRef(false);

  const query = useQuery();
  const d = query.get("d");
  const n = query.get("n");
  const c = query.get("c");

  const normalizedStatus = normalizeStatus(`${status} ${status_i18n}`);
  const isLostOrCanceled = ["lost", "perdido", "canceled", "cancelled", "cancelado"]
    .some((value) => normalizedStatus.includes(value));
  const isSettled = [
    "settled",
    "paid",
    "completed",
    "quitado",
    "pago",
    "liquidado",
    "concluido",
  ].some((value) => normalizedStatus.includes(value));
  const showInstallmentCheckboxes = !isLostOrCanceled && !isSettled;
  const opensAutomatically = [
    "active",
    "confirmed",
    "requested",
    "open",
    "overdue",
    "late",
    "ativo",
    "aberto",
    "atraso",
    "solicitado",
  ].some((value) => normalizedStatus.includes(value));
  const allowsAllInstallments = [
    "active",
    "open",
    "overdue",
    "late",
    "ativo",
    "aberto",
    "atraso",
  ].some((value) => normalizedStatus.includes(value));
  const allowsFirstInstallment = ["confirmed", "requested", "solicitado"]
    .some((value) => normalizedStatus.includes(value));

  const isInstallmentOverdue = (installment: Parcela) => {
    return installment.overdue || moment(installment.due_date).isBefore(moment(), "day");
  };

  const overdueInstallments = sortByDueDate(
    parcelasNaoPagas.filter(isInstallmentOverdue),
  );
  const openInstallments = sortByDueDate(
    parcelasNaoPagas.filter((installment) => !isInstallmentOverdue(installment)),
  );
  const paidInstallments = sortByDueDate(parcelasPagas);

  const canSelectInstallment = (installment: Parcela) => {
    return allowsAllInstallments || (allowsFirstInstallment && installment.installment_number === 1);
  };

  const selectableInstallments = parcelasNaoPagas.filter(canSelectInstallment);
  const selectedSelectableInstallments = selectableInstallments.filter(
    (installment) => installment.selected,
  );
  const hasSomeSelectableInstallmentsSelected = selectedSelectableInstallments.length > 0;
  const allSelectableInstallmentsAreSelected = selectableInstallments.length > 0
    && selectableInstallments.every((installment) => installment.selected);
  const hasPartiallySelectedInstallments = hasSomeSelectableInstallmentsSelected
    && !allSelectableInstallmentsAreSelected;
  const totalSelecionado = selectedSelectableInstallments.reduce(
    (total, installment) => total + installment.total,
    0,
  );
  const paidValue = Number(valorPago) || 0;
  const outstandingValue = Number(valor) || 0;
  const totalInstallmentPlanValue = Math.max(paidValue, 0) + Math.max(outstandingValue, 0);
  const paidValuePercentage = totalInstallmentPlanValue > 0
    ? (Math.max(paidValue, 0) / totalInstallmentPlanValue) * 100
    : 0;
  const outstandingValuePercentage = totalInstallmentPlanValue > 0
    ? (Math.max(outstandingValue, 0) / totalInstallmentPlanValue) * 100
    : 0;

  const ticketGeneration = async () => {
    setLoadingBoleto(true);
    try {
      const response = await ConsultarService.parcelas(id);
      const unpaid = response.unpaid_installments || [];
      setHasLoadedInstallments(true);
      setShow(true);
      setParcelamentoId(response.payment_request_id);
      setParcelasPagas(response.paid_installments || []);
      setParcelasNaoPagas(unpaid);
      setOverdueInstallmentBilletDueDate(response.overdue_installment_billet_due_date);
      await generateBilletFor(unpaid.filter((installment) => installment.installment_number === 1), unpaid);
    } catch (err) {
      setErrorBoleto(t("translations.billetConfirmation.checkError"));
    } finally {
      setLoadingBoleto(false);
    }
  };

  const removeModalClass = () => {
    if (document.body.className === "modal-open") {
      document.body.removeAttribute("style");
      document.body.removeAttribute("class");
    }
  };

  useEffect(() => {
    if (d && n && c && Number(c) === id) {
      autoLoadAttempted.current = true;
      ticketGeneration();
    }
    removeModalClass();
  }, []);

  const loadInstallments = async () => {
    setLoading(true);
    try {
      await ConsultarService
        .parcelas(id)
        .then((response) => {
          setError("");
          setHasLoadedInstallments(true);
          setShow(true);
          setParcelamentoId(response.payment_request_id);
          setOverdueInstallmentBilletDueDate(response.overdue_installment_billet_due_date);
          setParcelasPagas(response.paid_installments || []);
          setParcelasNaoPagas(response.unpaid_installments || []);

        })
        .catch((err) => {
          if (isServerError(err)) {
            setError(err.error);
          } else {
            console.error(err);
          }
        })
        .finally(() => setLoading(false));
    } catch (err) {
      setLoading(false);
    }
  };

  const handleLoadParcelas = () => {
    if (hasLoadedInstallments) {
      setShow((currentShow) => !currentShow);
      return;
    }

    loadInstallments();
  };

  useEffect(() => {
    if ((opened || opensAutomatically) && !autoLoadAttempted.current) {
      autoLoadAttempted.current = true;
      loadInstallments();
    }
  }, [opened, opensAutomatically]);

  const handleSelect = (selectedInstallment: Parcela) => () => {
    if (!canSelectInstallment(selectedInstallment)) {
      return;
    }

    setParcelasNaoPagas((currentInstallments) => currentInstallments.map((installment) => (
      installment.installment_number === selectedInstallment.installment_number
        ? { ...installment, selected: !installment.selected }
        : installment
    )));
  };

  const handleSelectAll = () => {
    const shouldSelect = !allSelectableInstallmentsAreSelected;
    setParcelasNaoPagas((currentInstallments) => currentInstallments.map((installment) => (
      canSelectInstallment(installment)
        ? { ...installment, selected: shouldSelect }
        : installment
    )));
  };

  const handleVisualizarInscricoes = async () => {
    try {
      setLoadingInscricoes(true);
      await ConsultarService
        .inscricoes(parcelamentoId)
        .then((response) => {
          setErrorInscricoes("");
          setInscricoes(response);
          setShowModalInscricoes(true);
        })
        .catch((err) => {
          if (isServerError(err)) {
            setErrorInscricoes(err.error);
            setShowModalInscricoes(true);
          } else {
            console.error(err);
          }
        })
        .finally(() => setLoadingInscricoes(false));
    } catch (err) {
      setLoadingInscricoes(false);
    }
  };

  const handleBillet = async (installmentNumbers: number[], pix = false) => {
    try {
      setLoadingBoleto(true);
      setPixPayment(pix);
      setErrorBoleto("");
      const payload: GenerateInstallmentsBilletsPayload = {
        payment_request_id: parcelamentoId,
        installments: installmentNumbers,
      };

      await AgilisServices
        .generateInstallmentsBillets(payload)
        .then((response) => {
          setShowGenerateBilletDueTodayAlert(false);
          setAlertOverdueInstallments(false);
          setPayment(response);
        })
        .catch((err) => {
          if (isServerError(err)) {
            setErrorBoleto(err.error);
          } else {
            setErrorBoleto(t("translations.billetConfirmation.generateError"));
          }
        })
        .finally(() => {
          setShowModalBoleto(true);
          setLoadingBoleto(false);
        });
    } catch (err) {
      setErrorBoleto(t("translations.billetConfirmation.generateError"));
      setLoadingBoleto(false);
    }
  };

  const generateBilletFor = async (selectedInstallments: Parcela[], allInstallments = parcelasNaoPagas, skipMissingOverdue = false, pix = false) => {
    if (!selectedInstallments.length) return;
    setLoadingBoleto(true);
    setErrorBoleto("");
    setPayment(undefined);
    setPixPayment(pix);
    try {
      const selectedNumbers = selectedInstallments.map((installment) => installment.installment_number);
      const previous = await AgilisServices.previousBillets({ payment_request_id: id, installments: selectedNumbers });
      const overdue = allInstallments.filter(isInstallmentOverdue);
      const includesEveryOverdueInstallment = overdue.every((installment) => selectedNumbers.includes(installment.installment_number));
      const mixedDueDates = selectedInstallments.some(isInstallmentOverdue)
        && selectedInstallments.some((installment) => !isInstallmentOverdue(installment));
      const missingOverdue = !includesEveryOverdueInstallment && !skipMissingOverdue && !mixedDueDates;
      setParcelasSelecionadas(selectedInstallments);
      setParcelasAtrasadas(overdue);
      setHasPreviousBillet(previous);
      setShowOverdueWarning(mixedDueDates);
      setShowGenerateBilletDueTodayAlert(previous || mixedDueDates);
      setAlertOverdueInstallments(missingOverdue);
      if (previous || mixedDueDates || missingOverdue) {
        setShowModalBoleto(true);
      } else {
        await handleBillet(selectedNumbers, pix);
      }
    } catch (err) {
      setErrorBoleto(t("translations.billetConfirmation.checkError"));
    } finally {
      setLoadingBoleto(false);
    }
  };

  const handleGerarBoleto = () => {
    generateBilletFor(parcelasNaoPagas.filter((installment) => installment.selected));
  };

  const handlePagarComPix = () => {
    generateBilletFor(parcelasNaoPagas.filter((installment) => installment.selected), parcelasNaoPagas, false, true);
  };

  const handleHideBoleto = () => {
    if (loadingBoleto) return;
    setShowModalBoleto(false);
    setPayment(undefined);
    setShowGenerateBilletDueTodayAlert(false);
    setAlertOverdueInstallments(false);
  };

  const requestedAt = moment(data).locale("pt-br").format("DD [de] MMMM [de] YYYY");

  const renderInstallment = (
    installment: Parcela,
    installmentStatus: "pago" | "aberto" | "atraso",
  ) => {
    const displayedStatus = installmentStatus === "pago"
      ? "pago"
      : normalizedStatus.includes("lost") || normalizedStatus.includes("perdido")
        ? "perdido"
        : normalizedStatus.includes("canceled") || normalizedStatus.includes("cancelado")
          ? "cancelado"
          : installmentStatus;

    return (
      <ItemParcela
        key={`${displayedStatus}-${installment.installment_number}`}
        parcela={installment.installment_number}
        totalParcelas={totalParcelas}
        valor={installment.total}
        data={installment.due_date}
        billet_date={installment.generate_billet_date || null}
        status={displayedStatus}
        selected={Boolean(installment.selected)}
        disabled={installmentStatus === "pago" || !canSelectInstallment(installment)}
        loadingBillet={loadingBoleto}
        showCheckbox={showInstallmentCheckboxes}
        onSelect={handleSelect(installment)}
        onPayWithPix={() => generateBilletFor([installment], parcelasNaoPagas, false, true)}
        onGenerateBillet={() => generateBilletFor([installment])}
      />
    );
  };

  return (
    <article className={style.itemInstallmentPlan}>
      <header
        className={`${style.installmentHeader} ${show ? style.installmentHeaderExpanded : ""}`}
      >
        <div className={style.headerMainContent}>
          <Button
            variant="text"
            size="extra-small"
            iconButton
            type="button"
            className={style.collapseButton}
            aria-label={show ? t(`${keyI18n}.aria.collapse`) : t(`${keyI18n}.aria.expand`)}
            tooltip={show ? t(`${keyI18n}.tooltips.minimize`) : t(`${keyI18n}.tooltips.maximize`)}
            tooltipPlacement="bottom"
            disabled={loading}
            onClick={handleLoadParcelas}
            icon={loading
              ? <span className={style.buttonLoader}><Loader /></span>
              : <span className="material-symbols-sharp" aria-hidden="true">{show ? "arrow_drop_down" : "arrow_right"}</span>}
          />

          <div className={style.headerTextActions}>
            <div className={`${style.headerTitle} pgm-label-large`}>
              {t(`${keyI18n}.labels.paymentRequest`, { number: numero })}
              <span className={style.headerDivider} aria-hidden="true">|</span>
              <span>{t(`${keyI18n}.labels.requestedAt`, { date: requestedAt })}</span>
            </div>

            <Button
              variant="outlined"
              shape="square"
              size="extra-small"
              iconButton
              type="button"
              className={style.informationButton}
              aria-label={t(`${keyI18n}.aria.viewAdministrativeInscription`)}
              tooltip={t(`${keyI18n}.tooltips.viewAdministrativeInscription`)}
              tooltipPlacement="bottom"
              disabled={loadingInscricoes}
              onClick={handleVisualizarInscricoes}
              icon={<span className="material-symbols-sharp" aria-hidden="true">info</span>}
            />
          </div>
        </div>

        <div
          className={`${style.headerSelection} ${
            showInstallmentCheckboxes ? "" : style.headerSelectionWithoutCheckbox
          }`}
        >
          {!hasSomeSelectableInstallmentsSelected && (
            <div className={style.paymentValues}>
              <div className={style.paymentValueLabels}>
                <Tooltip text={t(`${keyI18n}.tooltips.paidValue`)} placement="bottom">
                  <span className={`${style.paymentValue} pgm-label-medium`}>
                    <span className={`${style.valueIndicator} ${style.paidIndicator}`} aria-hidden="true" />
                    {Format.currency(paidValue)}
                  </span>
                </Tooltip>

                <Tooltip
                  text={isLostOrCanceled
                    ? t(`${keyI18n}.tooltips.unpaidValue`)
                    : t(`${keyI18n}.tooltips.payableValue`)}
                  placement="bottom"
                >
                  <span className={`${style.paymentValue} pgm-label-medium`}>
                    <span
                      className={`${style.valueIndicator} ${
                        isLostOrCanceled ? style.unpaidIndicator : style.outstandingIndicator
                      }`}
                      aria-hidden="true"
                    />
                    {Format.currency(outstandingValue)}
                  </span>
                </Tooltip>
              </div>

              <div className={style.valueBars} aria-hidden="true">
                <span
                  className={`${style.valueBar} ${style.paidBar}`}
                  style={{ width: `${paidValuePercentage}%` }}
                />
                <span
                  className={`${style.valueBar} ${
                    isLostOrCanceled ? style.unpaidBar : style.outstandingBar
                  }`}
                  style={{ width: `${outstandingValuePercentage}%` }}
                />
              </div>
            </div>
          )}

          {hasSomeSelectableInstallmentsSelected && (
            <div className={style.selectedActions}>
              <span className="pgm-label-large">
                {t(`${keyI18n}.labels.selectedValue`)}{" "}
                <span className={`${style.selectedValue} pgm-body-medium`}>
                  {Format.currency(totalSelecionado)}
                </span>
              </span>
              <Button
                type="button"
                variant="tonal"
                size="extra-small"
                icon={<span className="material-symbols-sharp" aria-hidden="true">qr_code_2</span>}
                disabled={loadingBoleto}
                onClick={handlePagarComPix}
              >
                {loadingBoleto && pixPayment
                  ? <Loader label={t(`${keyI18n}.actions.generatingPayment`)} />
                  : t(`${keyI18n}.actions.payPix`)}
              </Button>
              <Button
                type="button"
                variant="tonal"
                size="extra-small"
                icon={<span className="material-symbols-sharp" aria-hidden="true">barcode</span>}
                disabled={loadingBoleto}
                onClick={handleGerarBoleto}
              >
                {loadingBoleto && !pixPayment
                  ? <Loader label={t(`${keyI18n}.actions.generatingBillet`)} />
                  : t(`${keyI18n}.actions.generateBillet`)}
              </Button>
            </div>
          )}

          {showInstallmentCheckboxes && (
            <label className={style.checkboxControl}>
              <input
                type="checkbox"
                aria-label={t(`${keyI18n}.aria.selectAll`, { number: numero })}
                aria-checked={hasPartiallySelectedInstallments ? "mixed" : allSelectableInstallmentsAreSelected}
                checked={allSelectableInstallmentsAreSelected}
                data-indeterminate={hasPartiallySelectedInstallments}
                disabled={!hasLoadedInstallments || selectableInstallments.length === 0}
                onChange={handleSelectAll}
              />
              {hasPartiallySelectedInstallments && (
                <span
                  className={`material-symbols-sharp ${style.indeterminateIcon}`}
                  aria-hidden="true"
                >
                  check_indeterminate_small
                </span>
              )}
            </label>
          )}
        </div>
      </header>

      {show && (
        <>
          <div className={style.installments}>
            {overdueInstallments.map((installment) => renderInstallment(installment, "atraso"))}
            {openInstallments.map((installment) => renderInstallment(installment, "aberto"))}
            {paidInstallments.map((installment) => renderInstallment(installment, "pago"))}
          </div>

        </>
      )}

      <Modal
        title={t(`${keyI18n}.modals.inscriptions`)}
        show={showModalInscricoes}
        onHide={() => setShowModalInscricoes(false)}
      >
        {errorInscricoes && <Alert>{errorInscricoes}</Alert>}
        {inscricoes.map((inscricao) => (
          <div key={inscricao.id} className={style.modalInscription}>
            <div className={`${style.modalInscriptionOption} pgm-body-medium`}>
              <strong className="pgm-title-medium">
                {t(`${keyI18n}.labels.administrativeInscription`)}{" "}
                {inscricao.administrative_inscription}
              </strong>
              {inscricao.inscription_date && (
                <span>
                  {t(`${keyI18n}.labels.inscriptionDate`)}{" "}
                  {Format.date(inscricao.inscription_date)}
                </span>
              )}
              <span>
                {t(`${keyI18n}.labels.balance`)}{" "}
                {Format.currency(inscricao.balance)}
              </span>
            </div>
          </div>
        ))}
      </Modal>

      <PaymentModal
        open={showModalBoleto && Boolean(payment)}
        payment={payment}
        title={t(`${keyI18n}.modals.installmentsPayment`)}
        onClose={handleHideBoleto}
      />

      <BilletConfirmationModal
        open={showModalBoleto && !payment && (showGenerateBilletDueTodayAlert || alertOverdueInstallments)}
        loading={loadingBoleto}
        hasPreviousBillet={hasPreviousBillet}
        showOverdueWarning={showOverdueWarning}
        overdueDueDate={overdueInstallmentBilletDueDate}
        overdueInstallments={alertOverdueInstallments ? parcelasAtrasadas : []}
        paymentRequestNumber={numero}
        selectedNumbers={parcelasSelecionadas.map((installment) => installment.installment_number)}
        error={errorBoleto}
        onClose={handleHideBoleto}
        onConfirm={() => handleBillet(parcelasSelecionadas.map((installment) => installment.installment_number), pixPayment)}
        onGenerateOverdue={(installment) => generateBilletFor([installment], parcelasNaoPagas, true, pixPayment)}
      />
      {errorBoleto && !(showModalBoleto && !payment && (showGenerateBilletDueTodayAlert || alertOverdueInstallments)) && <Alert>{errorBoleto}</Alert>}

      {error && (
        <div className={style.error}>
          <Alert>{error}</Alert>
        </div>
      )}
    </article>
  );
}
